import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { onAuthStateChanged, signInWithPopup, signOut, type User } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db, demoAdminEnabled, firebaseConfigured, googleProvider } from './firebase';

interface Session { user: User | null; authorized: boolean; demoAdmin: boolean; loading: boolean; signIn: () => Promise<void>; signOutUser: () => Promise<void>; enterDemoAdmin: () => void; }
const AuthContext = createContext<Session | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [authorized, setAuthorized] = useState(false);
  const [demoAdmin, setDemoAdmin] = useState(false);
  const [loading, setLoading] = useState(firebaseConfigured);
  useEffect(() => {
    if (!auth) { setLoading(false); return; }
    return onAuthStateChanged(auth, async u => {
      setUser(u); setAuthorized(false);
      if (u && db) {
        try { const role = await getDoc(doc(db, 'admins', u.uid)); setAuthorized(role.exists() && role.data().active === true); }
        catch { setAuthorized(false); }
      }
      setLoading(false);
    });
  }, []);
  const value = useMemo<Session>(() => ({
    user, authorized, demoAdmin, loading,
    signIn: async () => { if (!auth || !googleProvider) throw new Error('Firebase no configurado'); await signInWithPopup(auth, googleProvider); },
    signOutUser: async () => { setDemoAdmin(false); setAuthorized(false); if (auth) await signOut(auth); },
    enterDemoAdmin: () => { if (demoAdminEnabled) setDemoAdmin(true); }
  }), [user, authorized, demoAdmin, loading]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
export function useAuth() { const value = useContext(AuthContext); if (!value) throw new Error('AuthProvider requerido'); return value; }
